/**
 * Reads claims from a JWT payload (no verification — UI hint only).
 */
const readTokenPayload = (token) => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const toNonNegativeInt = (value) => {
  if (value == null || value === '') {
    return null;
  }

  const id = Number(value);
  if (!Number.isInteger(id) || id < 0) {
    return null;
  }

  return id;
};

const pickClaimString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return null;
};

/** Display fields sometimes present on the access token. UI hint only. */
export const getUserProfileHintFromToken = (token) => {
  const payload = readTokenPayload(token);
  if (!payload) {
    return null;
  }

  const name = pickClaimString(payload.name, payload.full_name, payload.fullName);
  const username = pickClaimString(
    payload.username,
    payload.user_name,
    payload.preferred_username,
    payload.unique_name
  );
  const phoneNumber = pickClaimString(
    payload.phone_number,
    payload.phone,
    payload.phoneNumber
  );

  if (!name && !username && !phoneNumber) {
    return null;
  }

  return {
    name,
    username,
    phone_number: phoneNumber,
  };
};

export const getUserIdFromToken = (token) => {
  const payload = readTokenPayload(token);
  if (!payload) {
    return null;
  }

  const candidates = [
    payload.web_user_id,
    payload.user_id,
    payload.id,
    payload.sub,
    payload.userId,
    payload.webUserId,
    payload.platformUserId,
  ];

  for (const value of candidates) {
    const id = Number(value);
    if (!Number.isNaN(id) && id > 0) {
      return id;
    }
  }

  return null;
};

export const getCompanyIdFromToken = (token) => {
  const payload = readTokenPayload(token);
  if (!payload) {
    return null;
  }

  return toNonNegativeInt(
    payload.companyId ??
      payload.company_id ??
      payload.fk_company_id ??
      payload.CompanyId ??
      payload.selectedCompanyId ??
      payload.contextCompanyId ??
      payload.targetCompanyId ??
      payload.company?.companyId ??
      payload.company?.company_id
  );
};

/** Platform supervisor: JWT companyId is 0, or a tenant context token with platformAdmin. */
export const isPlatformSupervisorToken = (token) => {
  const payload = readTokenPayload(token);
  if (!payload) {
    return false;
  }

  if (payload.platformAdmin === true) {
    return true;
  }

  return getCompanyIdFromToken(token) === 0;
};

/** True when JWT `exp` is missing-safe false, or already expired / about to expire. */
export const isAccessTokenExpired = (token, skewSeconds = 30) => {
  const payload = readTokenPayload(token);
  if (!payload || payload.exp == null || payload.exp === '') {
    return false;
  }

  const expiresAtMs = Number(payload.exp) * 1000;
  if (!Number.isFinite(expiresAtMs)) {
    return false;
  }

  return Date.now() >= expiresAtMs - skewSeconds * 1000;
};
